import { useLocalSearchParams, Link } from "expo-router"
import { View, Pressable } from "react-native"
import * as Clipboard from "expo-clipboard"
import * as Burnt from "burnt"

import { ScrollView, Header, ActivityIndicator, Text, MCIcon, AdjustStockModal, UpdateLowStockThresholdModal } from "@src/components"
import { trpc } from "@src/utils/trpc"
import { ar } from "@src/i18n/ar"
import { formatPrice, priceFromDBFormat, sharedStyles } from "@src/utils"
import { formatWeight } from "@white-labels/utils"
import { Show, useObservable } from "@legendapp/state/react"
import { ProductImageView } from "@src/components/product-image-view"

export default function InventoryDetailsPage() {
  const { id, productId } = useLocalSearchParams<{ id: string; productId: string }>()
  const storagePlaceId = Number(id)
  const product = trpc.products.get.useQuery({ id: Number(productId) }, { retry: false })

  const showAdjustModal$ = useObservable(false)
  const showThresholdModal$ = useObservable(false)

  const isWeightProduct = product.data?.type === "WEIGHT_PRODUCT"
  const quantity = product.data?.inventory?.quantity ?? 0
  const lowStockThreshold = product.data?.inventory?.lowStockThreshold
  const averageCost = product.data?.inventory?.averageCost

  function fmtQty(val: number) {
    return isWeightProduct ? `${(val / 1000).toFixed(2)} ${ar.kg}` : val.toString()
  }

  // Low stock check
  const isLowStock =
    product.data?.status === "active" &&
    product.data?.inventory?.quantity != null &&
    product.data?.inventory?.lowStockThreshold != null &&
    product.data.inventory.quantity < product.data.inventory.lowStockThreshold

  const header = (
    <Header
      title={product.data?.name ?? ar.inventoryDetails}
      right={
        <View className="flex-row h-full">
          <Link href={`/dashboard/inventory/storage/${storagePlaceId}/product/${productId}/track-inventory`} asChild>
            <Pressable className="h-full px-3 justify-center active:opacity-70">
              <MCIcon name="history" size={24} color="#6B7280" />
            </Pressable>
          </Link>
          <Link href={`/dashboard/products/${productId}/edit`} asChild>
            <Pressable className="h-full px-3 justify-center active:opacity-70">
              <MCIcon name="pencil" size={24} color="#6B7280" />
            </Pressable>
          </Link>
        </View>
      }
    />
  )

  // Loading state
  if (product.isLoading) {
    return (
      <>
        {header}
        <ActivityIndicator className="mt-4" />
      </>
    )
  }

  // Error state
  if (product.isError || !product.data) {
    return (
      <>
        {header}
        <View className="flex-1 items-center justify-center p-4">
          <MCIcon name="alert-circle-outline" size={48} color="#EF4444" />
          <Text className="text-lg text-red-600 mt-2 text-center">{ar.somethingWentWrong}</Text>
        </View>
      </>
    )
  }

  return (
    <>
      {header}
      <ScrollView className="flex-1 bg-gray-50" contentContainerStyle={sharedStyles.contentContainer} onRefresh={() => product.refetch()}>
        {product.data.status === "inactive" && (
          <View className="bg-gray-100 border border-gray-300 rounded p-3 mb-4">
            <Text className="text-gray-700 font-bold text-center">🚫 {ar.inactive}</Text>
          </View>
        )}

        {/* Low Stock Warning */}
        {isLowStock && (
          <View className="bg-red-100 border border-red-200 rounded p-3 mb-4">
            <Text className="text-red-800 font-bold text-center">⚠️ {ar.lowStockWarning}</Text>
          </View>
        )}

        {/* Cost & Pricing Card */}
        <View className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
          <View className="flex-row justify-between items-center py-2 border-b border-gray-100 mb-3">
            <Text className="text-lg font-bold text-gray-900">{ar.costAndPricing}</Text>
            {product.data.status !== "inactive" && (
              <Link href={`/dashboard/products/${productId}`} asChild>
                <Pressable className="border border-blue-200 px-3 py-1.5 bg-blue-50">
                  <Text className="text-blue-700 text-sm">{ar.productDetails}</Text>
                </Pressable>
              </Link>
            )}
          </View>

          {/* upc */}
          <View className="flex-row justify-between items-center py-2 border-b border-gray-100">
            <Text className="text-gray-600">{ar.barcode}</Text>
            <Text
              className="text-base"
              onLongPress={() => {
                Clipboard.setStringAsync(product.data.upc)
                Burnt.toast({ title: ar.barcodeCopied })
              }}
            >
              {product.data.upc}
            </Text>
          </View>

          {/* Selling Price */}
          <View className="flex-row justify-between items-center py-2 border-b border-gray-100">
            <Text className="text-gray-600">{ar.price}</Text>
            <Text className="text-xl font-bold text-green-600">
              {priceFromDBFormat(product.data.price)} {ar.egp}
            </Text>
          </View>

          {/* Cost */}
          <View className="flex-row justify-between items-center py-2 border-b border-gray-100">
            <Text className="text-gray-600">{ar.cost}</Text>
            <Text className="text-lg font-medium text-blue-600">
              {formatPrice(product.data.cost)} {ar.egp}
            </Text>
          </View>

          {averageCost ? (
            <View className="flex-row justify-between items-center py-2 border-b border-gray-100">
              <Text className="text-gray-600">{ar.averageCost}</Text>
              <Text className="text-lg font-medium text-purple-600">
                {priceFromDBFormat(averageCost)} {ar.egp}
              </Text>
            </View>
          ) : null}

          {/* Cost Note */}
          <View className="bg-blue-50 rounded p-2 mt-3">
            <Text className="text-xs text-blue-600 leading-relaxed">{ar.costNote}</Text>
          </View>
        </View>

        {/* Stock Levels Card */}
        <View className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
          <Text className="text-lg font-bold text-gray-900 mb-3">{ar.stockLevels}</Text>

          {/* Current Quantity */}
          <View className="flex-row justify-between items-center py-2 border-b border-gray-100">
            <Text className="text-gray-600">{ar.qty}</Text>
            <Text className={`text-2xl font-bold ${isLowStock ? "text-red-600" : "text-green-600"}`}>{fmtQty(quantity)}</Text>
          </View>

          {/* Low Stock Threshold */}
          <View className="flex-row justify-between items-center py-2 border-b border-gray-100">
            <Text className="text-gray-600">{ar.lowStockThreshold}</Text>
            <Text className="text-lg font-medium text-yellow-600">{lowStockThreshold != null ? fmtQty(lowStockThreshold) : "—"}</Text>
          </View>

          {/* Action Buttons */}
          <View className="flex-row gap-3 mt-4">
            <Pressable className="flex-1 h-12 rounded items-center justify-center active:opacity-80 bg-blue-600" onPress={() => showAdjustModal$.set(true)}>
              <Text className="text-white font-bold">{ar.adjustStock}</Text>
            </Pressable>

            <Pressable className="flex-1 h-12 rounded items-center justify-center active:opacity-80 bg-yellow-500" onPress={() => showThresholdModal$.set(true)}>
              <Text className="text-white font-bold text-sm">{ar.setLowStockThreshold}</Text>
            </Pressable>
          </View>
        </View>

        {/* Derived Products Section */}
        {(product.data.derived?.length ?? 0) > 0 && (
          <View className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
            <Text className="text-lg font-bold text-gray-900 mb-3">{ar.derivedProducts}</Text>
            {product.data.derived?.map((derived) => (
              <Link key={derived.id} href={`/dashboard/inventory/storage/${storagePlaceId}/product/${derived.id}`} asChild>
                <Pressable className="flex-row justify-between items-center py-3 border-b border-gray-100 active:bg-gray-50">
                  <View className="flex-1">
                    <Text className="text-base font-medium text-gray-900">{derived.name ?? "N/A"}</Text>
                    <Text className="text-sm text-gray-500">
                      {derived.type === "WEIGHT_DEFINED_PRODUCT" ? formatWeight(derived.value ?? 0, ar.kg, ar.gms) : (derived.value ?? "N/A")}
                    </Text>
                  </View>
                  <MCIcon name="chevron-left" size={28} color="#9CA3AF" />
                </Pressable>
              </Link>
            ))}
          </View>
        )}

        {product.data.baseProductId && (
          <Link href={`/dashboard/inventory/storage/${storagePlaceId}/product/${product.data.baseProductId}`} asChild>
            <Pressable className="bg-white rounded-lg border border-gray-200 p-4 mb-4 flex-row items-center justify-between active:bg-gray-50">
              <View className="flex-1">
                <Text className="text-base font-medium text-blue-600">{ar.viewBaseProduct}</Text>
                <Text className="text-sm text-gray-500">{product.data.value}</Text>
              </View>
              <MCIcon name="chevron-left" size={28} color="#9CA3AF" />
            </Pressable>
          </Link>
        )}
      </ScrollView>

      {/* Modals */}
      <Show if={showAdjustModal$}>
        <AdjustStockModal
          onClose={() => showAdjustModal$.set(false)}
          productId={Number(productId)}
          storagePlaceId={storagePlaceId}
          currentQuantity={quantity}
          isWeightProduct={isWeightProduct}
        />
      </Show>

      <Show if={showThresholdModal$}>
        <UpdateLowStockThresholdModal
          onClose={() => showThresholdModal$.set(false)}
          productId={Number(productId)}
          storagePlaceId={storagePlaceId}
          currentThreshold={lowStockThreshold ?? null}
        />
      </Show>
    </>
  )
}
